import React, { useState, useRef, KeyboardEvent, useEffect } from "react";

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  isLoading?: boolean;
  suggestedPrompts?: string[];
  placeholder?: string;
  primaryColor?: string;
}

const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  isLoading = false,
  suggestedPrompts = [],
  placeholder = "Write your message here...",
  primaryColor = "#3b82f6",
}) => {
  const [message, setMessage] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Always keep focus on textarea
  useEffect(() => {
    // Set initial focus when component mounts
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  // Re-focus after loading state changes
  useEffect(() => {
    if (!isLoading && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isLoading]);

  const handleSendMessage = () => {
    if (message.trim() && !isLoading) {
      onSendMessage(message.trim());
      setMessage("");

      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
        
        // Set focus back to textarea with a slight delay to ensure it happens after state updates
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.focus();
          }
        }, 10);
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);

    // Auto-resize textarea
    const textarea = e.target;
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  const handlePromptClick = (prompt: string) => {
    onSendMessage(prompt);
    
    // Focus textarea after clicking a prompt
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, 10);
  };

  // Dynamic styles for the send button
  const sendButtonStyle = {
    backgroundColor: message.trim() && !isLoading ? primaryColor : undefined,
  };

  // Dynamic styles for suggested prompts (when enabled)
  const promptButtonStyle = {
    borderColor: primaryColor,
    color: primaryColor,
  };

  return (
    <div className="skit-ai-llm-bg-white">
      <div className="skit-ai-llm-flex skit-ai-llm-items-center skit-ai-llm-p-3 skit-ai-llm-gap-2 skit-ai-llm-border-t skit-ai-llm-border-gray-200">
        <div className="skit-ai-llm-flex-1 skit-ai-llm-flex skit-ai-llm-items-center skit-ai-llm-relative">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyDown}
            onBlur={() => {
              // Re-focus when textarea loses focus (except for clicking elsewhere intentionally)
              if (!isLoading) {
                setTimeout(() => textareaRef.current?.focus(), 10);
              }
            }}
            placeholder={placeholder}
            className="skit-ai-llm-w-full skit-ai-llm-resize-none skit-ai-llm-min-h-[40px] skit-ai-llm-max-h-[120px] skit-ai-llm-py-2 skit-ai-llm-px-2 skit-ai-llm-outline-none skit-ai-llm-border-none focus:skit-ai-llm-ring-0 focus:skit-ai-llm-outline-none skit-ai-llm-text-xs skit-ai-llm-rounded-[4px]"
            disabled={isLoading}
            rows={1}
            autoFocus
            style={{ fontSize: "12px" }}
          />
        </div>

        <button
          onClick={handleSendMessage}
          disabled={!message.trim() || isLoading}
          className={`skit-ai-llm-p-2 skit-ai-llm-rounded-full ${
            !message.trim() || isLoading
              ? "skit-ai-llm-text-gray-400 skit-ai-llm-bg-gray-100"
              : "skit-ai-llm-text-white hover:skit-ai-llm-bg-opacity-90"
          } skit-ai-llm-transition-colors skit-ai-llm-flex-shrink-0`}
          style={sendButtonStyle}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="22" y1="2" x2="11" y2="13"></line>
            <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
          </svg>
        </button>
      </div>

      {suggestedPrompts && suggestedPrompts.length > 0 && (
        <div className="skit-ai-llm-flex skit-ai-llm-gap-2 skit-ai-llm-p-3 skit-ai-llm-flex-wrap">
          {suggestedPrompts.map((prompt, index) => (
            <button
              key={index}
              onClick={() => handlePromptClick(prompt)}
              className="skit-ai-llm-px-3 skit-ai-llm-py-1 skit-ai-llm-border skit-ai-llm-rounded-full hover:skit-ai-llm-bg-gray-100 skit-ai-llm-transition-colors skit-ai-llm-flex-shrink-0 skit-ai-llm-text-xs"
              disabled={isLoading}
              style={promptButtonStyle}
            >
              {prompt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChatInput;